import {
	Card,
	Divider,
	Button,
	Modal,
	Form,
	message,
	Input,
	Select,
	Spin,
} from 'antd';
import moment from 'moment';
import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';
import { getAdminWalletBalance } from '../../methods/redux/reducers/general';
import {
	retrieveSingleSwapRequest,
	revertSwapTransaction,
	updateSwapStatus,
} from '../../methods/redux/reducers/swap';
import { retrieveToken } from '../../methods/utils/auth';
import TransactionItem from '../../transactions/transaction-statments/TransactionItem';
import BlockchainProcessRequest from './BlockchainProcessRequest';
import ProcessSwapRequest from './ProcessSwapRequest';
import UnAssignSwapRequest from './UnAssignSwapRequest';
import NativeNumber from '../../component/Numeral';

interface IStyle {
	statusColor: any;
}
const SingleSwapItemPageStyle = styled.div<IStyle>`
	& .indication {
		height: 5px;
		background-color: ${(p) => p.statusColor};
		margin-bottom: 20px;
	}

	& .details {
		& > * {
			display: inline-block;
			margin-right: 20px;
			margin-bottom: 15px;
		}
	}

	& .label {
		color: #aaa;
		font-style: italic;
		font-size: 14px;
	}

	& .actions {
		margin-top: 10px;
		& > * {
			display: inline-block;
			margin-right: 20px;
		}
	}
`;

export const StyledFormItem = styled(Form.Item)`
	.ant-row {
		display: block;
	}
`;

const SingleSwapItemPage = () => {
	const dispatch = useAppDispatch();
	const { swapItemId } = useParams();

	const [form] = Form.useForm();

	const { singleSwapRequest } = useAppSelector((store) => store.swap);

	const [isModalVisible, setIsModalVisible] = useState(false);
	const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
	const [isLoadingRevert, setIsLoadingRevert] = useState(false);
	const [isLoadingUpdate, setIsLoadingUpdate] = useState(false);
	const [state, setState] = useState({
		_id: '',
	});

	useEffect(() => {
		const auth = retrieveToken();
		setState({ ...state, ...auth.user });
	}, []);

	useEffect(() => {
		if (swapItemId) {
			dispatch(retrieveSingleSwapRequest({ swapTransactionId: swapItemId }));
		}
	}, [swapItemId]);

	useEffect(() => {
		if (
			state._id &&
			state._id.length > 0 &&
			singleSwapRequest.swapStatus === 'processing'
		) {
			dispatch(getAdminWalletBalance(state._id));
		}
	}, [state._id, singleSwapRequest.swapStatus]);

	const swapStatusColor = (status: any) => {
		switch (status) {
			case 'processing':
				return 'orange';
			case 'completed':
				return 'green';
			case 'hold':
				return 'maroon';
			default:
				return '#f3f3f3';
		}
	};

	const showModal = () => {
		setIsModalVisible(true);
	};

	const handleCancel = () => {
		setIsModalVisible(false);
		setIsUpdateModalVisible(false);
		form.resetFields();
	};

	const showUpdateModal = () => {
		setIsUpdateModalVisible(true);
	};

	const handleSubmit = async (values: any) => {
		try {
			setIsLoadingRevert(true);

			await dispatch(
				revertSwapTransaction({
					processingSwapId: swapItemId,
					title: values.title,
				})
			);

			handleCancel();
		} catch (error) {
			message.error('Failed to revert swap');
		} finally {
			setIsLoadingRevert(false);
		}
	};

	const handleSubmitUpdate = async (values: any) => {
		try {
			setIsLoadingUpdate(true);

			await dispatch(
				updateSwapStatus({
					processingSwapId: swapItemId,
					swapStatus: values.swapStatus,
				})
			);

			handleCancel();
		} catch (error) {
			message.error('Failed to revert swap');
		} finally {
			setIsLoadingUpdate(false);
		}
	};

	return (
		<SingleSwapItemPageStyle
			statusColor={swapStatusColor(singleSwapRequest.swapStatus)}
		>
			<Card>
				<div className="indication" />
				<div style={{ display: 'flex', justifyContent: 'space-between' }}>
					<h1>
						{singleSwapRequest.baseCurrency.symbol} Swap for{' '}
						<Link to={`/user-profile/${singleSwapRequest.userId?._id}`}>
							@{singleSwapRequest.userId?.username}
						</Link>
					</h1>

					<Button
						type="primary"
						size="middle"
						disabled={singleSwapRequest.swapStatus !== 'hold'}
						onClick={() => showModal()}
					>
						Revert Swap
					</Button>
				</div>
				<p>Status: {singleSwapRequest.swapStatus}</p>
				<button
					type="button"
					disabled={
						singleSwapRequest.swapStatus !== 'pending' &&
						singleSwapRequest.swapStatus !== 'deleted' &&
						singleSwapRequest.swapStatus !== 'hold' &&
						singleSwapRequest.swapStatus !== 'cancelled' &&
						singleSwapRequest.swapStatus !== 'review'
					}
					onClick={showUpdateModal}
				>
					Update Status
				</button>

				<Divider />

				<div className="details">
					{singleSwapRequest.swapStatus === 'hold' && (
						<div>
							<p>{singleSwapRequest.swapHoldReason}</p>
							<p className="label">Swap Hold Reason</p>
						</div>
					)}
					<div>
						<div style={{ display: 'flex' }}>
							<img
								width={20}
								height={20}
								style={{ marginRight: 5 }}
								src={singleSwapRequest.baseCurrency?.iconUrl}
								alt="swap fee token"
							/>
							<p>
								<NativeNumber value={singleSwapRequest.baseCurrencyAmount} />{' '}
								{singleSwapRequest.baseCurrency.symbol}
							</p>
						</div>
						<p className="label">Base Currency</p>
					</div>
					<div>
						<div style={{ display: 'flex' }}>
							<img
								width={20}
								height={20}
								style={{ marginRight: 5 }}
								src={singleSwapRequest.baseCurrency?.iconUrl}
								alt="swap fee token"
							/>
							<p>
								<NativeNumber
									value={singleSwapRequest.baseConvertRequestAmount}
								/>{' '}
								{singleSwapRequest.baseCurrency.symbol}
							</p>
						</div>
						<p className="label">Base Convert Amount</p>
					</div>
					<div>
						<p>{singleSwapRequest.quoteCurrency.symbol}</p>
						<p className="label">Quote Currency</p>
					</div>
					<div>
						<p>
							<NativeNumber
								value={singleSwapRequest.resultQuoteCurrencyAmountLower}
							/>{' '}
							{singleSwapRequest.quoteCurrency.symbol}
						</p>
						<p className="label">Quote Lower Amount</p>
					</div>
					<div>
						<p>
							<NativeNumber
								value={singleSwapRequest.resultQuoteCurrencyAmountUpper}
							/>{' '}
							{singleSwapRequest.quoteCurrency.symbol}
						</p>
						<p className="label">Quote Amount (Upper)</p>
					</div>
					<div>
						<p>{singleSwapRequest.slippage}</p>
						<p className="label">Slippage</p>
					</div>
					<div>
						<p>{singleSwapRequest.transactionSpeed}</p>
						<p className="label">Transaction Speed</p>
					</div>
					{singleSwapRequest.swapProtocol && (
						<div>
							<p>{singleSwapRequest.swapProtocol?.name}</p>
							<p className="label">Swap Protocol</p>
						</div>
					)}
					{singleSwapRequest.swapFeeTokenId && (
						<div>
							<div style={{ display: 'flex' }}>
								<img
									width={20}
									height={20}
									style={{ marginRight: 5 }}
									src={singleSwapRequest.swapFeeTokenId?.iconUrl}
									alt="swap fee token"
								/>
								<p>{singleSwapRequest.swapFeeTokenId?.symbol}</p>
							</div>
							<p className="label">Swap Fee Token</p>
						</div>
					)}
					<div>
						{singleSwapRequest.processedByUserId ? (
							<Link
								to={`/user-profile/${singleSwapRequest.processedByUserId?._id}`}
							>
								@
								{singleSwapRequest.processedByUserId?.username ||
									singleSwapRequest.processedByUserId?.email}
							</Link>
						) : (
							'--'
						)}
						<p className="label">Processed By</p>
					</div>
					<div>
						<p>
							{moment(singleSwapRequest.createdAt).format(
								'MMM DD, YY / hh:mm:ss a'
							)}
						</p>
						<p className="label">Created At</p>
					</div>
					<div>
						<p>{singleSwapRequest.cronTouch ? 'True' : 'False'}</p>
						<p className="label">Cron Touch</p>
					</div>
				</div>

				<Divider />
				<div>
					<p>
						<strong>Update Swap Status</strong>
					</p>
					<br />
				</div>

				{singleSwapRequest.swapStatus !== 'completed' && (
					<>
						<br />
						<Divider />

						<p>
							<strong>Actions</strong>
						</p>
						<br />
						<p style={{ color: 'var(--red-color)' }}>
							Note that the transaction fee for Blockchain Swap should not be
							more than{' '}
							<strong>
								{singleSwapRequest.swapData.transactionFeeInUSD} USD
							</strong>
						</p>

						<br />

						<div className="actions">
							<div>
								<ProcessSwapRequest request={singleSwapRequest} />
							</div>
							<div>
								<BlockchainProcessRequest request={singleSwapRequest} />
							</div>
							<div>
								<UnAssignSwapRequest
									request={singleSwapRequest}
									adminId={state._id}
								/>
							</div>
						</div>

						<br />
					</>
				)}
				<Divider />

				<p>
					<strong>Swap Data</strong>
				</p>
				<br />

				<div className="details">
					<div>
						<p>
							<NativeNumber
								value={singleSwapRequest.swapData.baseCurrencyBalance}
							/>{' '}
							{singleSwapRequest.baseCurrency.symbol}
						</p>
						<p className="label">Base Currency Balance</p>
					</div>
					<div>
						<p>
							<NativeNumber
								value={
									singleSwapRequest.swapData.transactionFeeInBaseCurrencyAmount
								}
							/>{' '}
							{singleSwapRequest.baseCurrency.symbol}
						</p>
						<p className="label">Transaction Fee (Base)</p>
					</div>
					<div>
						<p>
							<NativeNumber
								value={singleSwapRequest.swapData.transactionFeeInToken}
							/>{' '}
							{singleSwapRequest.swapData.feeTokenCurrencySymbol}
						</p>
						<p className="label">Transaction Fee in Token</p>
					</div>
					<div>
						<p>{singleSwapRequest.swapData.transactionFeeInUSD} USD</p>
						<p className="label">Transaction Fee in USD</p>
					</div>
					<div>
						<p>{singleSwapRequest.swapData.feeCurrencyBalance}</p>
						<p className="label">Fee Currency Balance</p>
					</div>
					<div>
						{singleSwapRequest?.swapData?.tokenTaxInBaseCurrency ? (
							<p>
								{singleSwapRequest.swapData.tokenTaxInBaseCurrency?.amount} (
								{singleSwapRequest.swapData.tokenTaxInBaseCurrency?.percentage}
								%)
							</p>
						) : (
							'--'
						)}
						<p className="label">Token Tax In Base Currency</p>
					</div>
					<div>
						<p>{singleSwapRequest.swapUseTokenAsFee ? 'True' : 'False'}</p>
						<p className="label">Swap Token As Fee</p>
					</div>
					<div>
						<p>{singleSwapRequest.userSwapUseTokenAsFee ? 'True' : 'False'}</p>
						<p className="label">User Swap Token As Fee</p>
					</div>
					<div>
						<p>{singleSwapRequest.swapData.routeText}</p>
						<p className="label">Route</p>
					</div>
					<div>
						<p>{singleSwapRequest.swapData.details}</p>
						<p className="label">Details</p>
					</div>
				</div>

				<Divider />

				<p>
					<strong>Rate Revalidation</strong>
				</p>
				<br />

				<div className="details">
					<div>
						<p>
							<NativeNumber value={singleSwapRequest.feeCurrencyBalance} />{' '}
							{singleSwapRequest.feeCurrencyBalance}
						</p>
						<p className="label">Fee Currency Balance</p>
					</div>
					<div>
						<p>
							<NativeNumber value={singleSwapRequest.feeDiscountPercentage} />{' '}
							{singleSwapRequest.feeDiscountPercentage}
						</p>
						<p className="label">Fee Discount Percentage</p>
					</div>
					<div>
						<p>{singleSwapRequest.feeDiscountRule}</p>
						<p className="label">Fee Discount Rule</p>
					</div>
					<div>
						<p>
							<NativeNumber value={singleSwapRequest.gasFeeInNativeToken} />{' '}
							{singleSwapRequest.gasFeeInNativeToken}
						</p>
						<p className="label">Gas Fee In Native Token</p>
					</div>

					<div>
						<p>{singleSwapRequest.gasFeeInUSD} USD</p>
						<p className="label">Gas Fee In USD</p>
					</div>
					<div>
						<p>{singleSwapRequest.liquidityProviderFeeInBaseCurrency}</p>
						<p className="label">Liquidity Provider Fee In Base Currency</p>
					</div>

					<div>
						<p>{singleSwapRequest.swapRateChange}</p>
						<p className="label">Swap Rate Change</p>
					</div>

					<div>
						<p>{singleSwapRequest.swapRateChangePercent} %</p>
						<p className="label">swap Rate Change Percentage</p>
					</div>

					<div>
						<p>{singleSwapRequest?.priceImpact || '--'}</p>
						<p className="label">Price Impact</p>
					</div>

					<div>
						<p>
							{singleSwapRequest?.swapRateRevalidation?.success
								? 'True'
								: 'False'}
						</p>
						<p className="label">Success</p>
					</div>
					<div>
						<p>
							{singleSwapRequest?.swapRateRevalidation?.thresholdBreached
								? 'True'
								: 'False'}
						</p>
						<p className="label">Thresh hold Breached</p>
					</div>
					<div>
						<p>
							<NativeNumber
								value={
									singleSwapRequest.swapRateRevalidation?.baseCurrencyAmountUsed
								}
							/>{' '}
							USD
						</p>
						<p className="label">Base Currency Amount Used</p>
					</div>
					<div>
						<p>
							<NativeNumber
								value={
									singleSwapRequest.swapRateRevalidation
										?.originalQuoteCurrencyAmountLower
								}
							/>{' '}
							USD
						</p>
						<p className="label">Original Quote Currency Amount Lower</p>
					</div>

					<div>
						<p>
							<NativeNumber
								value={
									singleSwapRequest.swapRateRevalidation
										?.originalQuoteCurrencyAmountLower
								}
							/>{' '}
							USD
						</p>
						<p className="label">Original Quote Currency Amount Lower</p>
					</div>
					<div>
						<p>
							<NativeNumber
								value={
									singleSwapRequest.swapRateRevalidation
										?.revalidatedQuoteCurrencyAmountLower
								}
							/>{' '}
						</p>
						<p className="label">Revalidated Quote Currency Amount Lower</p>
					</div>
				</div>
				<div>
					<p>
						{moment(
							singleSwapRequest?.swapRateRevalidation?.revalidatedAt
						).format('MMM DD, YY / hh:mm:ss a')}
					</p>
					<p className="label">revalidatedAt</p>
				</div>

				<Divider />

				<div>
					<p>
						<strong>Swap Fee Details</strong>
					</p>

					<div className="details">
						<div>
							<p>{singleSwapRequest.feeDiscountRule}</p>
							<p className="label">Fee Discount Rule</p>
						</div>
						<div>
							<p>{singleSwapRequest.feeDiscountPercentage}%</p>
							<p className="label">Fee Discount Percentage</p>
						</div>
						<div>
							<p>{singleSwapRequest.gasFeeInNativeToken}</p>
							<p className="label">Gas Fee</p>
						</div>
						<div>
							<p>{singleSwapRequest.gasFeeInUSD} USD</p>
							<p className="label">Gas Fee</p>
						</div>
						<div>
							<p>
								{singleSwapRequest.liquidityProviderFeeInBaseCurrency}{' '}
								{singleSwapRequest.baseCurrency?.symbol}
							</p>
							<p className="label">Liquidity Provider Fee</p>
						</div>
						<div>
							<p>
								{Number(singleSwapRequest.swapData.transactionFeeInUSD) -
									Number(singleSwapRequest.gasFeeInUSD)}{' '}
								USD
							</p>
							<p className="label">Fee Difference</p>
						</div>
					</div>
				</div>

				<Divider />

				<p>
					<strong>Associated Transaction Statements</strong>
				</p>
				{singleSwapRequest.associatedSwapTransactions.map(
					(transaction: any) => (
						<TransactionItem key={transaction._id} transaction={transaction} />
					)
				)}
			</Card>

			{/* Modal for revert swap */}
			<Modal
				title={`Revert Swap `}
				visible={isModalVisible}
				onCancel={handleCancel}
				footer={null}
			>
				<Form form={form} onFinish={handleSubmit}>
					<p>This sends the money back to the user wallet</p>
					<br />

					<StyledFormItem
						name="title"
						label="Title"
						rules={[
							{ required: true, message: 'Please input the reversal title!' },
						]}
					>
						<Input placeholder="Enter the title of reversal reason" />
					</StyledFormItem>

					<Divider />

					<Form.Item>
						<Button type="primary" htmlType="submit" loading={isLoadingRevert}>
							{/* {isLoadingRevert ? 'Reverting...' : 'Revert Swap now'} */}
							{isLoadingRevert ? <Spin size="small" /> : 'Revert Swap now'}
						</Button>
					</Form.Item>
				</Form>
			</Modal>

			{/* Modal for Update swap status */}
			<Modal
				title={`Update swap status `}
				visible={isUpdateModalVisible}
				onCancel={handleCancel}
				footer={null}
			>
				<Form form={form} onFinish={handleSubmitUpdate}>
					<p>Update swap status</p>
					<br />

					<StyledFormItem
						name="swapStatus"
						label="swapStatus"
						rules={[
							{ required: true, message: 'Please select a swap status!' },
						]}
					>
						<Select
							placeholder="Select a swap status"
							options={[
								{ label: 'review', value: 'review' },
								{ label: 'pending', value: 'pending' },
								{ label: 'deleted', value: 'deleted' },
								{ label: 'hold', value: 'hold' },
								{ label: 'cancelled', value: 'cancelled' },
							]}
						/>
					</StyledFormItem>

					<Divider />

					<Form.Item>
						<Button type="primary" htmlType="submit" loading={isLoadingUpdate}>
							{isLoadingUpdate ? 'Updating...' : 'Update Status'}
						</Button>
					</Form.Item>
				</Form>
			</Modal>
		</SingleSwapItemPageStyle>
	);
};

export default SingleSwapItemPage;
